import React, { useEffect, useState } from "react";
import "./joinroompage.css";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setIsRoomHost, setRoomId, setIdentity, setConnectOnlyWithAudio } from "../store/actions";
import JoinRoomTitle from "./JoinRoomTitle";
import JoinRoomContent from "./JoinRoomContent";
import PreviewWindow from "./previewWindow";
import { getRoomExists } from "../utils/api";

const JoinRoompage = (props) => {
  const { setIdentityAction, setRoomIdAction, setIsRoomHostAction, isRoomHost, connectOnlyWithAudio, setConnectOnlyWithAudio } = props;
  const search = useLocation().search;
  const navigate = useNavigate();
  const [full, setFull] = useState(false);
  const [roomError, setRoomError] = useState(null);
  const [joinError, setJoinError] = useState(null); // ✨ Error to show inside PreviewWindow
  const [showPreview, setShowPreview] = useState(false);
  const [meetingTiming, setMeetingTiming] = useState(null);
  
  // Helper function to check if meeting has ended
  const isMeetingEnded = (date, toTime) => {
    if (!date || !toTime) return false;
    
    const meetingEndDateTime = new Date(`${date}T${toTime}:00`);
    const now = new Date();
    
    return now > meetingEndDateTime;
  };

  // Helper function to check if meeting hasn't started yet
  // Only blocks joining if more than 1 minute before start time
  const isMeetingNotStarted = (date, fromTime) => {
    if (!date || !fromTime) return false;
    
    const meetingStartDateTime = new Date(`${date}T${fromTime}:00`);
    const now = new Date();
    
    // Allow joining up to 1 minute before the meeting starts
    const oneMinuteBefore = new Date(meetingStartDateTime.getTime() - 60 * 1000);
    
    return now < oneMinuteBefore;
  };

  // Helper function to format time remaining or elapsed
  const getTimeStatus = (date, fromTime, toTime) => {
    const now = new Date();
    
    if (!date || !fromTime || !toTime) return null;
    
    const startDateTime = new Date(`${date}T${fromTime}:00`);
    const endDateTime = new Date(`${date}T${toTime}:00`);
    
    if (now > endDateTime) {
      // Meeting has ended
      const elapsed = now - endDateTime;
      const hours = Math.floor(elapsed / (1000 * 60 * 60));
      const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `ended ${days} day${days > 1 ? 's' : ''} ago`;
      } else if (hours > 0) {
        return `ended ${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else if (minutes > 0) {
        return `ended ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else {
        return 'ended just now';
      }
    } else if (now < startDateTime) {
      // Meeting hasn't started yet
      const remaining = startDateTime - now;
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `starts in ${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else if (minutes > 0) {
        return `starts in ${minutes} minute${minutes > 1 ? 's' : ''}`;
      } else {
        return 'starting soon';
      }
    } else {
      // Meeting is ongoing
      return 'in progress';
    }
  };
  
  useEffect(() => {
    const urlparams = new URLSearchParams(search);
    const roomId = urlparams.get("roomId");
    const userName = urlparams.get('name');
    const fromTime = urlparams.get('fromTime');
    const toTime = urlparams.get('toTime');
    const date = urlparams.get('date');
    
    // Auto-join flow for meeting links with both roomId and name
    if (roomId && userName) {
      console.log("🔗 Meeting link detected");
      console.log("Room ID:", roomId);
      console.log("User Name:", userName);
      console.log("Meeting Time:", fromTime, "-", toTime);
      console.log("Meeting Date:", date);
      
      // ✨ Check if meeting has already ended — block at page level (no point showing preview)
      if (date && toTime && isMeetingEnded(date, toTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        console.error("❌ Meeting has ended");
        setRoomError(`This meeting ${status}. The meeting was scheduled for ${fromTime} - ${toTime} on ${date}.`);
        return;
      }

      // ✨ Meeting hasn't started yet — show preview but block joining via joinError
      if (date && fromTime && isMeetingNotStarted(date, fromTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        console.log(`⏰ Meeting ${status} - will show error in preview window`);
        setJoinError(`This meeting ${status}. You can join up to 1 minute before the start time.`);
      }
      
      // Set as non-host and set initial state
      setIsRoomHostAction(false);
      setRoomIdAction(roomId);
      setIdentityAction(userName);
      
      // Store meeting timing from URL
      if (fromTime || toTime) {
        setMeetingTiming({ fromTime, toTime, date });
      }
      
      // Check if room exists
      const checkRoom = async () => {
        try {
          const response = await getRoomExists(roomId);
          console.log("Room check:", response);
          
          const { roomExists, full } = response;
          
          if (full) {
            console.error("❌ Room is full");
            setFull(true);
            setRoomError("Meeting is full. Please try again later.");
            return;
          }
          
          // Let JoinRoomContent handle the preview window
          console.log("✅ Room check passed - ready to show preview");
        } catch (error) {
          console.error("❌ Error checking room:", error);
          setRoomError("Error checking meeting. Please try again.");
        }
      };
      
      checkRoom();
    } else if (roomId) {
      // Only roomId provided - manual join flow
      setIsRoomHostAction(false);
      setRoomIdAction(roomId);
      
      // Check for timing parameters even in manual join
      const fromTime = urlparams.get('fromTime');
      const toTime = urlparams.get('toTime');
      const date = urlparams.get('date');
      
      // Check if past meeting — block at page level
      if (date && toTime && isMeetingEnded(date, toTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        setRoomError(`This meeting ${status}. The meeting was scheduled for ${fromTime} - ${toTime} on ${date}.`);
        return;
      }

      // ✨ Meeting hasn't started yet — show preview but block joining via joinError
      if (date && fromTime && isMeetingNotStarted(date, fromTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        console.log(`⏰ Meeting ${status} - will show error in preview window`);
        setJoinError(`This meeting ${status}. You can join up to 1 minute before the start time.`);
      }
      
      if (fromTime || toTime) {
        setMeetingTiming({ fromTime, toTime, date });
      }
    }
  }, [search, setIsRoomHostAction, setRoomIdAction, setIdentityAction]);

  const urlparams = new URLSearchParams(search);
  const autoRoomId = urlparams.get("roomId") || "";
  const autoName = urlparams.get("name") || "";
  const urlFromTime = urlparams.get("fromTime");
  const urlToTime = urlparams.get("toTime");
  const urlDate = urlparams.get("date");
  
  const handleShowPreview = (show, timing) => {
    setShowPreview(show);
    // Use timing from URL if not provided by component
    if (timing) {
      setMeetingTiming(timing);
    } else if (show && (urlFromTime || urlToTime)) {
      setMeetingTiming({ fromTime: urlFromTime, toTime: urlToTime, date: urlDate });
    } else if (!show) {
      setMeetingTiming(null);
    }
  };
  
  const handlePreviewJoin = () => {
    localStorage.setItem('participantsEnabled',true);
    // Navigate to room after preview, preserve timing in URL
    const params = new URLSearchParams();
    if (urlFromTime) params.append('fromTime', urlFromTime);
    if (urlToTime) params.append('toTime', urlToTime);
    if (urlDate) params.append('date', urlDate);
    
    const queryString = params.toString();
    navigate(`/room${queryString ? `?${queryString}` : ''}`);
  };

  const handlePreviewCancel = () => {
    // Close preview window
    setShowPreview(false);
    setMeetingTiming(null);
    window.close();
  };
  
  // Show error state if room is full, has errors, or meeting has ended
  if (roomError) {
    return (
      <div className="join_room_page_container">
        <div className="join_room_page_panel">
          <JoinRoomTitle isRoomHost={isRoomHost} />
          <div className="error-content">
            <div className="error_message_container">
              <p className="error_message_paragraph">{roomError}</p>
            </div>
            <button 
              onClick={() => {
                setRoomError(null);
                setFull(false);
                window.location.href = '/';
              }}
              className="join_room_success_button"
              style={{ marginTop: '20px' }}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="join_room_page_container">
        <div className="join_room_page_panel">
          <JoinRoomTitle isRoomHost={isRoomHost} />
          <JoinRoomContent
            autoRoomId={autoRoomId}
            autoName={autoName}
            onShowPreview={handleShowPreview}
          />
        </div>
      </div>
      
      {/* Preview window rendered outside the panel */}
      {showPreview && (
        <PreviewWindow
          onJoin={handlePreviewJoin}
          onCancel={handlePreviewCancel}
          connectOnlyWithAudio={connectOnlyWithAudio}
          setConnectOnlyWithAudio={setConnectOnlyWithAudio}
          meetingTiming={meetingTiming}
          joinError={joinError} // ✨ Pass early-join error to preview window
        />
      )}
    </>
  );
};

const mapStoreStateToProps = (state) => {
  return {
    ...state,
  };
};

const mapActionsToProps = (dispatch) => {
  return {
    setIsRoomHostAction: (isRoomHost) => dispatch(setIsRoomHost(isRoomHost)),
    setRoomIdAction: (roomId) => dispatch(setRoomId(roomId)),
    setIdentityAction: (identity) => dispatch(setIdentity(identity)),
    setConnectOnlyWithAudio: (onlyWithAudio) => dispatch(setConnectOnlyWithAudio(onlyWithAudio)),
  };
};

export default connect(mapStoreStateToProps, mapActionsToProps)(JoinRoompage);